import { useMemo, useState } from 'react'

import { encode, decode } from 'js-base64'

import { MESSAGING_POST_TYPE_SLUG } from '@bettermode/common/utils'
import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  CustomFieldType,
  MassActionRequest,
  MassActionRequestContext,
  Post,
  PostListFilterByEnum,
  PostListFilterByOperator,
  PostListOrderByEnum,
  PostStatus,
} from '@tribeplatform/gql-client/types'
import { CustomFieldSubtype } from '@tribeplatform/react-components/CustomField'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { PostActionItems, TagPills } from '@tribeplatform/react-components/Post'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useCreateMassActionRequest,
  useNetwork,
  usePosts,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useQuery } from '../../../hooks/useQuery.js'
import { useDebounce } from '../../../utils/useDebounce.js'
import { getFilterValue } from '../../PostsBlock/utils/index.js'
import { Table } from '../../Table/index.js'
import { tableColumnsFromCustomFields } from '../../Table/utils.js'
import { ConfirmMassActionModal } from '../MassActions/ConfirmMassActionModal.js'
import { getNormalizedAction } from '../MassActions/constants.js'
import { MassActionBanner } from '../MassActions/MassActionBanner.js'
import { MassActionModal } from '../MassActions/MassActionModal.js'
import {
  MassActionModalState,
  MassActionRequestAction,
} from '../MassActions/types.js'

export const Posts = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { push, replace } = useRouter()

  let initialState = {
    orderBy: {
      column: PostListOrderByEnum.createdAt,
      reverse: true,
    },
    query: null,
    filterBy: [],
    spaceId: undefined,
    postTypeId: null,
  }

  const { state: queryState } = useQuery()
  if (queryState) {
    try {
      initialState = { ...initialState, ...JSON.parse(decode(queryState)) }
    } catch (error) {
      logger.error('Could not parse initial state for members')
    }
  }

  const { mutateAsync: createMassActionRequest } = useCreateMassActionRequest()
  const [state, setState] = useState(initialState)

  const [massActionModalState, setMassActionModalState] =
    useState<MassActionModalState>({ type: 'closed' })
  const [massActionRequest, setMassActionRequest] =
    useState<MassActionRequest>(null)

  const { postTypeId, orderBy, filterBy } = state

  const debouncedQuery = useDebounce(state?.query, 300)

  const handleStateChange = (newState: typeof state) => {
    setState(newState)
    replace(`?state=${encode(JSON.stringify(state))}`)
  }

  const newFilterBy = filterBy.map(({ key, operator, value }) => {
    if (key === 'owner.member') {
      key = 'ownerId'
    }
    if (key === 'tags') {
      if (operator === PostListFilterByOperator.not) {
        value = getFilterValue(key, operator, value)
        operator = PostListFilterByOperator.nin
      } else {
        operator = PostListFilterByOperator.contains
      }
      key = 'tagIds'
    }
    if (key === 'space') {
      key = 'spaceId'
      operator = PostListFilterByOperator.equals
    }

    return {
      key: PostListFilterByEnum.publishedAt,
      keyString: key,
      operator: operator?.split(':')[0],
      value,
    }
  })

  let massActionFilterBy = [
    ...newFilterBy.map(f => {
      const { keyString, ...rest } = f
      return { ...rest, key: keyString }
    }),
    {
      key: 'status',
      operator: PostListFilterByOperator.equals,
      value: JSON.stringify(PostStatus.PUBLISHED),
    },
  ]

  if (postTypeId) {
    massActionFilterBy = [
      ...massActionFilterBy,
      {
        key: 'postTypeId',
        operator: PostListFilterByOperator.equals,
        value: JSON.stringify(postTypeId),
      },
    ]
  }

  const handleSelectMassAction = async (action: MassActionRequestAction) => {
    const requiresConfirmationActions: MassActionRequestAction[] = [
      MassActionRequestAction.Delete,
      MassActionRequestAction.Hide,
      MassActionRequestAction.Lock,
    ]
    if (requiresConfirmationActions.includes(action)) {
      try {
        const massActionRequest = await createMassActionRequest({
          input: {
            context: MassActionRequestContext.Post,
            action: getNormalizedAction(action),
            waitForConfirmation: true,
            filters: {
              query: state?.query,
              filterBy: massActionFilterBy,
            },
          },
        })
        setMassActionModalState({
          type: 'confirm',
          massActionRequest,
        })
      } catch (e) {
        const errors = (e as ClientError)?.response?.errors
        toast({
          title:
            errors?.[0]?.message ??
            $t({
              defaultMessage: 'Unable to perform mass action',
              id: 'MassAction.Failed',
            }),
          status: 'error',
        })
      }
    } else {
      setMassActionModalState({ type: 'immediate', action })
    }
  }

  const {
    data,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePosts({
    fields: {
      owner: {
        member: {
          profilePicture: 'basic',
        },
      },
      authMemberProps: 'all',
      mentions: 'basic',
      embeds: 'default',
      attachments: 'default',
      tags: 'basic',
      postType: 'basic',
      space: 'basic',
    },
    variables: {
      postTypeIds: postTypeId ? [postTypeId] : undefined,
      limit: 15,
      orderByString: orderBy?.column,
      reverse: orderBy?.reverse,
      filterBy: newFilterBy || [],
      query: debouncedQuery,
    },
    useInfiniteQueryOptions: { keepPreviousData: true },
  })

  const { data: totalPostsData } = usePosts({
    fields: 'basic',
    variables: {
      limit: 0,
    },
    useInfiniteQueryOptions: { keepPreviousData: true },
  })

  const totalPosts = totalPostsData?.pages?.[0]?.totalCount

  const posts = simplifyPaginatedResult<Post>(data)
  posts?.nodes?.forEach(post => {
    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    post.mappedFields = post?.fields?.reduce((result, cur) => {
      try {
        result[cur.key] = JSON.parse(cur.value)
      } catch (e) {
        logger.error('Error parsing member field value', e.message)
      }
      return result
    }, {})
  })

  const customColumns = useMemo(() => {
    if (!postTypeId) {
      return []
    }

    return tableColumnsFromCustomFields(
      network?.postTypes?.find(postType => postType.id === postTypeId)
        ?.postFields?.fields,
    )
  }, [network?.postTypes, postTypeId])

  const columns = useMemo(
    () => [
      {
        Header: $t({ defaultMessage: 'ID', id: 'Generics.ID' }),
        accessor: 'id',
        renderer: 'primary',
        visible: false,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Title', id: 'Generics.Title' }),
        accessor: 'title',
        renderer: 'postPrimary',
        visible: true,
        type: CustomFieldType.text,
        width: 'lg',
      },
      {
        Header: $t({ defaultMessage: 'Author', id: 'Generics.Author' }),
        accessor: 'owner.member',
        renderer: 'member',
        visible: true,
        type: CustomFieldType.relation,
        subtype: CustomFieldSubtype.MEMBER,
        width: 'md',
      },
      ...customColumns,
      {
        Header: $t({ defaultMessage: '{SPACE_CC}', id: 'Generics.Space' }),
        accessor: 'space',
        renderer: 'space',
        visible: true,
        width: 'md',
        type: CustomFieldType.relation,
        subtype: CustomFieldSubtype.SPACE,
      },
      {
        Header: $t({
          defaultMessage: 'CMS model',
          id: 'Generics.CMSModel',
        }),
        accessor: 'postType.name',
        visible: false,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: '{TAGS_CC}', id: 'Generics.Tags' }),
        accessor: 'tags',
        renderer: post => {
          return <TagPills tags={[...post?.tags].splice(0, 2)} truncate />
        },
        visible: false,
        width: 'md',
        type: CustomFieldType.relation,
        subtype: CustomFieldSubtype.TAG,
      },
      {
        Header: $t({
          defaultMessage: 'Published at',
          id: 'Generics.PublishedAt',
        }),
        accessor: 'publishedAt',
        renderer: 'date',
        visible: true,
        orderKey: PostListOrderByEnum.createdAt,
        width: 'sm',
        type: CustomFieldType.date,
      },
      {
        Header: $t({ defaultMessage: 'Replies', id: 'Generics.Replies' }),
        accessor: 'totalRepliesCount',
        renderer: 'number',
        visible: true,
        orderKey: PostListOrderByEnum.totalRepliesCount,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Reactions', id: 'Generics.Reactions' }),
        accessor: 'reactionsCount',
        renderer: 'number',
        visible: true,
        orderKey: PostListOrderByEnum.reactionsCount,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Slug', id: 'Generics.Slug' }),
        accessor: 'slug',
        renderer: 'primary',
        visible: false,
        width: 'md',
      },
      {
        Header: $t({
          defaultMessage: 'Description',
          id: 'Generics.Description',
        }),
        accessor: 'description',
        visible: false,
        width: 'lg',
        type: CustomFieldType.text,
      },
      {
        Header: $t({
          defaultMessage: 'Locked',
          id: 'Generics.Locked',
        }),
        accessor: 'locked',
        width: 'sm',
        visible: false,
        renderer: 'boolean',
        type: CustomFieldType.boolean,
      },
      {
        Header: $t({
          defaultMessage: 'Hidden',
          id: 'Generics.Hidden',
        }),
        accessor: 'isHidden',
        width: 'sm',
        visible: false,
        renderer: 'boolean',
        type: CustomFieldType.boolean,
      },
      {
        visible: true,
        accessor: 'actions',
        locked: true,
        width: 'collapse',
        renderer: (_, post) => (
          <Dropdown placement="bottom-end">
            {({ open }) => (
              <>
                <Dropdown.ButtonMinimal as="div">
                  <IconButton
                    variant="tertiaryNeutral"
                    size="sm"
                    rounded
                    icon={<SvgIcon name="dots-horizontal" />}
                  />
                </Dropdown.ButtonMinimal>
                <PostActionItems
                  post={post}
                  context="admin"
                  showShortcuts={open}
                  className="w-56"
                />
              </>
            )}
          </Dropdown>
        ),
      },
    ],
    [$t, customColumns],
  )

  return (
    <div className="w-full flex flex-col gap-3">
      <MassActionBanner massActionRequest={massActionRequest} />
      <Table
        isLastColumnSticky
        storageId={postTypeId ? `posts:${postTypeId}` : 'posts'}
        isLoading={isInitialLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        state={state}
        hasSearch
        massActions={[
          MassActionRequestAction.Move,
          MassActionRequestAction.Hide,
          MassActionRequestAction.Delete,
          MassActionRequestAction.AddTag,
          MassActionRequestAction.RemoveTag,
          MassActionRequestAction.Lock,
          MassActionRequestAction.UpdateOwner,
          MassActionRequestAction.UpdatePublishedAt,
        ]}
        onMassAction={handleSelectMassAction}
        massActionContext={MassActionRequestContext.Post}
        onStateChange={handleStateChange}
        keyboardControl
        entityType="post"
        customTotalCount={totalPosts}
        title={$t({ defaultMessage: 'Posts', id: 'Generics.Posts' })}
        filterBarPrefix={
          <Dropdown>
            <Dropdown.Button
              as="div"
              className="flex cursor-pointer items-center"
            >
              <span>
                {network?.postTypes?.find(
                  postType => postType?.id === postTypeId,
                )?.name ||
                  $t({
                    defaultMessage: 'All post types',
                    id: 'Admin.Posts.Filter.AllTypes',
                  })}
              </span>
            </Dropdown.Button>
            <Dropdown.Items className="divide-y divide-line-subdued">
              <div className="py-1">
                <Dropdown.Item
                  onClick={() =>
                    handleStateChange({ ...state, postTypeId: null })
                  }
                >
                  <T
                    defaultMessage="All post types"
                    id="Admin.Posts.Filter.AllTypes"
                  />
                </Dropdown.Item>
              </div>
              <div className="py-1">
                {network?.postTypes
                  ?.filter(
                    postType => postType?.slug !== MESSAGING_POST_TYPE_SLUG,
                  )
                  .map(postType => (
                    <Dropdown.Item
                      onClick={() =>
                        handleStateChange({
                          ...state,
                          postTypeId: postType?.id,
                        })
                      }
                      key={postType?.id}
                    >
                      {postType?.name}
                    </Dropdown.Item>
                  ))}
              </div>
            </Dropdown.Items>
          </Dropdown>
        }
        actions={
          <Button size="lg" variant="primary" onClick={() => push('/new')}>
            <T defaultMessage="Add post" id="Admin.Posts.Add" />
          </Button>
        }
      />

      {massActionModalState.type === 'confirm' && (
        <ConfirmMassActionModal
          open={massActionModalState.type === 'confirm'}
          onClose={() => setMassActionModalState({ type: 'closed' })}
          onSuccess={setMassActionRequest}
          massActionRequest={massActionModalState.massActionRequest}
        />
      )}

      {massActionModalState.type === 'immediate' && (
        <MassActionModal
          open={massActionModalState.type === 'immediate'}
          onClose={() => setMassActionModalState({ type: 'closed' })}
          context={MassActionRequestContext.Post}
          action={massActionModalState.action}
          count={data?.pages?.[0]?.totalCount}
          onSuccess={setMassActionRequest}
          filterBy={massActionFilterBy}
          query={state?.query}
        />
      )}
    </div>
  )
}
